// OTPVerification.jsx

import React, { useState, useEffect } from 'react';
import { Col, Row, Form, Button, Container } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  verifyOTP,
  resendOTP,
  clearOTPState,
} from '../../redux/slices/authSlice';
import { toast } from 'react-toastify';
import Logo from '../../assets/logo7.png';
import '../../styles/otp-verification.scss';

import { messaging } from '../../firebase';
import { getToken } from 'firebase/messaging';
import { serverUrlAuth } from '../../serverUrl';

const OTPVerification = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // ==========================================
  // OTP STATE
  // ==========================================

  const [otp, setOtp] = useState('');

  // Resend cooldown
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // OTP expiry
  const [expiryTimer, setExpiryTimer] = useState(300); // 5 minutes
  const [otpExpired, setOtpExpired] = useState(false);

  // ==========================================
  // REDUX STATE
  // ==========================================

  const {
    verifyLoading,
    resendLoading,
    error,
    challengeId,
    email: authEmail,
  } = useSelector((state) => state.auth);

  // ==========================================
  // LOCATION STATE FALLBACK
  // ==========================================

  const locationState = location.state || {};

  /*
   * Redux is the primary source.
   * location.state is used as a fallback in case
   * the page was navigated to with state.
   */

  const challenge_id = challengeId || locationState.challenge_id;

  const email = authEmail || locationState.email;

  // ==========================================
  // INITIAL PAGE CHECK
  // ==========================================

  useEffect(() => {
    console.log('OTP Verification Page');
    console.log('Challenge ID:', challenge_id);
    console.log('Email:', email);

    if (!challenge_id) {
      toast.error('Invalid verification request. Please login again.');

      navigate('/login');
      return;
    }

    return () => {
      dispatch(clearOTPState());
    };
  }, [challenge_id, email, navigate, dispatch]);

  // ==========================================
  // RESEND COUNTDOWN
  // ==========================================

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(interval);
    }

    setCanResend(true);
  }, [timer]);

  // ==========================================
  // OTP EXPIRY COUNTDOWN
  // ==========================================

  useEffect(() => {
    if (expiryTimer > 0 && !otpExpired) {
      const interval = setInterval(() => {
        setExpiryTimer((prev) => {
          if (prev <= 1) {
            setOtpExpired(true);

            toast.error('OTP has expired. Please request a new one.');

            return 0;
          }

          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [expiryTimer, otpExpired]);

  // ==========================================
  // RESET EXPIRY TIMER
  // ==========================================

  const resetExpiryTimer = () => {
    setExpiryTimer(300);
    setOtpExpired(false);
  };

  // ==========================================
  // HANDLE REDUX ERRORS
  // ==========================================

  useEffect(() => {
    if (!error) return;

    console.log('OTP Error:', error);

    let errorMessage = 'An error occurred';

    if (typeof error === 'string') {
      errorMessage = error;
    } else if (Array.isArray(error)) {
      errorMessage = error[0] || 'An error occurred';
    } else if (typeof error === 'object' && error !== null) {
      if (error.error && Array.isArray(error.error)) {
        errorMessage = error.error[0];
      } else if (error.error && typeof error.error === 'string') {
        errorMessage = error.error;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.detail) {
        errorMessage = error.detail;
      } else {
        const firstKey = Object.keys(error)[0];

        if (firstKey && error[firstKey]) {
          if (Array.isArray(error[firstKey])) {
            errorMessage = error[firstKey][0];
          } else {
            errorMessage = String(error[firstKey]);
          }
        }
      }
    }

    toast.error(errorMessage);
  }, [error]);

  // ==========================================
  // FIREBASE NOTIFICATION PERMISSION
  // ==========================================

  const requestNotificationPermission = async () => {
    try {
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        return;
      }

      const token = await getToken(messaging, {
        vapidKey:
          'BPJeaJai7hMaSndqhkJBlQd8c7tfhMQwOhpziJxZQVzREPG4u70ntQpw_syUXoRT6AHmge8bEb4IaiXbkaMADzc',

        serviceWorkerRegistration: window.fcmServiceWorker,
      });

      if (token) {
        localStorage.setItem('fcm_token', token);

        await serverUrlAuth.post('/notifications/save-push-token/', {
          token,
        });

        console.log('Push token saved:', token);
      }
    } catch (err) {
      console.error('Error getting push token:', err);
    }
  };

  // ==========================================
  // HANDLE OTP INPUT
  // ==========================================

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);

    setOtp(value);
  };

  // ==========================================
  // HANDLE OTP PASTE
  // ==========================================

  const handlePaste = (e) => {
    const pastedData = e.clipboardData.getData('text');

    const numbersOnly = pastedData.replace(/\D/g, '').slice(0, 6);

    if (numbersOnly) {
      setOtp(numbersOnly);
    }
  };

  // ==========================================
  // VERIFY OTP
  // ==========================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Make sure challenge exists
    if (!challenge_id) {
      toast.error('Invalid verification request. Please login again.');

      navigate('/login');
      return;
    }

    // Check expiry
    if (otpExpired) {
      toast.error('This OTP has expired. Please request a new one.');

      return;
    }

    // Validate OTP length
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP.');

      return;
    }

    console.log('Verifying OTP with challenge:', challenge_id);

    try {
      const result = await dispatch(
        verifyOTP({
          challenge_id,
          otp,
        }),
      ).unwrap();

      console.log('OTP Verification Response:', result);

      // ==========================================
      // SAVE TOKENS
      // ==========================================

      if (result.access) {
        localStorage.setItem('access_token', result.access);
      }

      if (result.refresh) {
        localStorage.setItem('refresh_token', result.refresh);
      }

      // ==========================================
      // SAVE USER DATA
      // ==========================================

      if (result.user) {
        localStorage.setItem('role', result.user.role);

        localStorage.setItem('full_name', result.user.full_name);
      }

      toast.success('Login successful! Welcome back.');

      // ==========================================
      // REQUEST NOTIFICATION PERMISSION
      // ==========================================

      await requestNotificationPermission();

      // ==========================================
      // REDIRECT BASED ON ROLE
      // ==========================================

      const redirectPath =
        result.user?.role === 'artisan'
          ? '/artisan-dashboard'
          : result.user?.role === 'admin'
            ? '/admin-dashboard'
            : '/mode-selection';

      setTimeout(() => {
        navigate(redirectPath);
      }, 1500);
    } catch (error) {
      console.error('OTP verification failed:', error);

      // Clear OTP input after failed attempt
      setOtp('');
    }
  };

  // ==========================================
  // RESEND OTP
  // ==========================================

  const handleResendOTP = async () => {
    // Make sure challenge exists
    if (!challenge_id) {
      toast.error('Invalid verification request. Please login again.');

      navigate('/login');
      return;
    }

    console.log('Resending OTP with challenge_id:', challenge_id);

    try {
      const result = await dispatch(
        resendOTP({
          challenge_id,
        }),
      ).unwrap();

      console.log('Resend OTP Response:', result);

      toast.success(result.message || 'New OTP has been sent to your email.');

      // ==========================================
      // RESET RESEND TIMER
      // ==========================================

      setTimer(60);
      setCanResend(false);

      // ==========================================
      // CLEAR OTP INPUT
      // ==========================================

      setOtp('');

      // ==========================================
      // RESET 5-MINUTE EXPIRY TIMER
      // ==========================================

      resetExpiryTimer();

      /*
       * IMPORTANT:
       *
       * We intentionally DO NOT update challenge_id
       * here.
       *
       * The backend should keep the same challenge_id
       * when resending and only replace the OTP.
       *
       * Example:
       *
       * Initial OTP:
       * challenge_id = ABC123
       *
       * Resend:
       * challenge_id = ABC123
       * new OTP = 654321
       */
    } catch (error) {
      console.error('Resend OTP error:', error);
    }
  };

  // ==========================================
  // BACK TO LOGIN
  // ==========================================

  const handleBackToLogin = () => {
    navigate('/login');
  };

  // ==========================================
  // FORMAT TIME
  // ==========================================

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);

    const secs = seconds % 60;

    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`;
  };

  // ==========================================
  // EXPIRY TIMER COLOR
  // ==========================================

  const getExpiryColor = () => {
    if (otpExpired) {
      return '#dc3545';
    }

    if (expiryTimer > 180) {
      return '#28a745';
    }

    if (expiryTimer > 60) {
      return '#ffc107';
    }

    return '#dc3545';
  };

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <Container fluid className="otp-verification-container">
      <Row className="otp-row">
        {/* ======================================
            LEFT SIDE
        ====================================== */}

        <Col md={6} className="otp-left">
          <div className="overlay"></div>

          <div className="otp-left-content">
            <Link to="/">
              <img src={Logo} alt="Artiza Logo" className="logo" />
            </Link>

            <h2 className="otp-title">Verification Code</h2>

            <p className="otp-subtitle">
              We've sent a 6-digit verification code to your email
            </p>

            <div className="otp-features">
              <div className="feature-item">
                <span className="feature-icon">📧</span>

                <span>Check your inbox</span>
              </div>

              <div className="feature-item">
                <span className="feature-icon">⏱️</span>

                <span>Valid for 5 minutes</span>
              </div>

              <div className="feature-item">
                <span className="feature-icon">🔒</span>

                <span>Secure verification</span>
              </div>
            </div>
          </div>
        </Col>

        {/* ======================================
            RIGHT SIDE
        ====================================== */}

        <Col md={6} className="otp-right">
          <div className="otp-form-container">
            <h2>Verify Your Email</h2>

            {/* EMAIL INFO */}

            <div className="otp-info-box">
              <p className="otp-email-info">
                We sent a verification code to:
                <br />
                <strong>{email || 'your email'}</strong>
              </p>
            </div>

            {/* ==================================
                EXPIRY TIMER
            ================================== */}

            <div className="otp-expiry-timer">
              <div className="timer-header">
                <span className="timer-label">⏱️ OTP Expires In</span>

                <span
                  className="timer-value"
                  style={{
                    color: getExpiryColor(),
                  }}
                >
                  {otpExpired ? 'Expired' : formatTime(expiryTimer)}
                </span>
              </div>
            </div>

            {/* ==================================
                OTP FORM
            ================================== */}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-4">
                <Form.Label>Enter 6-digit OTP</Form.Label>

                <Form.Control
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="Enter OTP"
                  className={`otp-input ${
                    otpExpired ? 'otp-input-expired' : ''
                  }`}
                  value={otp}
                  onChange={handleOtpChange}
                  onPaste={handlePaste}
                  maxLength={6}
                  autoFocus
                  required
                  disabled={verifyLoading || otpExpired}
                />

                <div className="otp-input-hint">
                  {otpExpired ? (
                    <span className="hint-error">
                      ⏰ OTP has expired. Please request a new one.
                    </span>
                  ) : (
                    <>Enter the 6-digit code sent to your email</>
                  )}
                </div>
              </Form.Group>

              {/* ==================================
                  VERIFY BUTTON
              ================================== */}

              <Button
                type="submit"
                className="w-100 verify-btn mb-3"
                disabled={verifyLoading || otp.length !== 6 || otpExpired}
              >
                {verifyLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Verifying...
                  </>
                ) : (
                  'Verify OTP'
                )}
              </Button>

              {/* ==================================
                  ACTIONS
              ================================== */}

              <div className="otp-actions">
                <div className="resend-section">
                  <span className="resend-text">Didn't receive the code?</span>

                  {canResend ? (
                    <Button
                      variant="link"
                      className="resend-link"
                      onClick={handleResendOTP}
                      disabled={resendLoading}
                    >
                      {resendLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Sending...
                        </>
                      ) : (
                        'Resend OTP'
                      )}
                    </Button>
                  ) : (
                    <span className="timer-text">Resend in {timer}s</span>
                  )}
                </div>

                {/* BACK TO LOGIN */}

                <Button
                  variant="link"
                  className="back-to-login-link"
                  onClick={handleBackToLogin}
                >
                  ← Back to Login
                </Button>
              </div>
            </Form>

            {/* ==================================
                FOOTER
            ================================== */}

            <div className="otp-footer">
              <p className="otp-help-text">
                <small>
                  If you're having trouble receiving the code,
                  <br />
                  please check your spam folder or try again.
                </small>
              </p>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default OTPVerification;
